import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Empty, Field, Badge, ConfirmDialog } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { fmtDate, todayISO } from "../lib/format";

const EMPTY = { name: "", quantity: 1, serial: "", delivered_on: "", returned_on: "", notes: "" };

const equipmentBody = (f) => {
  const body = { ...f, quantity: Number(f.quantity || 1) };
  for (const k of ["serial", "delivered_on", "returned_on", "notes"]) body[k] = body[k] || null;
  return body;
};

function useEquipmentForm(open, workerId, item, onDone) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF(item ? { ...EMPTY, ...item } : { ...EMPTY, delivered_on: todayISO() });
  }, [open, item]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = equipmentBody(f);
    try {
      if (item?.id) await api.put(`/workers/equipment/${item.id}`, body);
      else await api.post(`/workers/${workerId}/equipment`, body);
      toast.success("✓ Dotazione salvata");
      onDone();
    } catch (err) {
      toast.error(`✕ ${errMsg(err)}`);
    } finally {
      setBusy(false);
    }
  };
  return { f, set, busy, submit };
}

function EquipmentDialog({ open, onOpenChange, workerId, item, onSaved }) {
  const { f, set, busy, submit } = useEquipmentForm(open, workerId, item, () => { onOpenChange(false); onSaved(); });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover max-w-xl" data-testid="equipment-form">
        <DialogHeader><DialogTitle className="font-heading">{item?.id ? "Modifica dotazione" : "Nuova dotazione"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-3">
          <Field label="Attrezzo / dotazione *" className="sm:col-span-2"><Input required value={f.name} onChange={set("name")} placeholder="Trapano, casco, imbracatura…" data-testid="equipment-name-input" /></Field>
          <Field label="Quantità *"><Input type="number" min="1" required value={f.quantity} onChange={set("quantity")} data-testid="equipment-quantity-input" /></Field>
          <Field label="Matricola / N. serie"><Input value={f.serial || ""} onChange={set("serial")} data-testid="equipment-serial-input" /></Field>
          <Field label="Data consegna"><Input type="date" value={f.delivered_on || ""} onChange={set("delivered_on")} data-testid="equipment-delivered-input" /></Field>
          <Field label="Data restituzione"><Input type="date" value={f.returned_on || ""} onChange={set("returned_on")} data-testid="equipment-returned-input" /></Field>
          <Field label="Note" className="sm:col-span-2"><Textarea value={f.notes || ""} onChange={set("notes")} data-testid="equipment-notes-input" /></Field>
          <DialogFooter className="sm:col-span-2 gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
            <Button type="submit" disabled={busy} data-testid="equipment-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EquipmentRow({ it, canEdit, onEdit, onDelete }) {
  return (
    <tr className="border-b border-border/50" data-testid={`equipment-row-${it.id}`}>
      <td className="py-2.5 pr-3 font-medium">{it.name}{it.notes && <div className="text-xs text-muted-foreground font-normal">{it.notes}</div>}</td>
      <td className="pr-3 num">{it.quantity}</td>
      <td className="pr-3 num text-xs">{it.serial || "—"}</td>
      <td className="pr-3 num text-xs">{fmtDate(it.delivered_on) || "—"}</td>
      <td className="pr-3">{it.returned_on
        ? <Badge className="bg-slate-800 border-slate-600 text-slate-300">Restituito {fmtDate(it.returned_on)}</Badge>
        : <Badge className="bg-amber-950 border-amber-600 text-amber-200">In uso</Badge>}</td>
      {canEdit && (
        <td className="text-right whitespace-nowrap">
          <Button size="icon" variant="ghost" onClick={() => onEdit(it)} data-testid={`equipment-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => onDelete(it)} data-testid={`equipment-delete-${it.id}`}><Trash2 className="h-4 w-4" /></Button>
        </td>
      )}
    </tr>
  );
}

export default function EquipmentPanel({ workerId, items, canEdit, reload }) {
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);
  const inUse = items.filter((i) => !i.returned_on).reduce((s, i) => s + i.quantity, 0);
  const remove = async () => {
    try {
      await api.delete(`/workers/equipment/${del.id}`);
      toast.success("✓ Dotazione eliminata");
      setDel(null);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="equipment-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="text-sm text-muted-foreground" data-testid="equipment-in-use-count"><Wrench className="h-4 w-4 inline mr-1" />{inUse} pezzi attualmente affidati</div>
        {canEdit && <Button size="sm" onClick={() => setEdit({})} data-testid="equipment-add-btn"><Plus className="h-4 w-4 mr-1" />Aggiungi dotazione</Button>}
      </div>
      {!items.length ? <Empty title="Nessun attrezzo o dotazione affidata" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="equipment-table">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
              <th className="py-2">Attrezzo / dotazione</th><th>Q.tà</th><th>Matricola</th><th>Consegna</th><th>Stato</th>{canEdit && <th />}
            </tr></thead>
            <tbody>{items.map((it) => <EquipmentRow key={it.id} it={it} canEdit={canEdit} onEdit={setEdit} onDelete={setDel} />)}</tbody>
          </table>
        </div>
      )}
      <EquipmentDialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)} workerId={workerId} item={edit?.id ? edit : null} onSaved={reload} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title={`Eliminare "${del?.name}"?`} description="La dotazione verrà rimossa dall'elenco dell'operaio." onConfirm={remove} />
    </div>
  );
}
